import { Image } from "expo-image";
import React, { createContext, useContext, useSyncExternalStore } from "react";
import { captureException } from "@utils/captureException";

// ---------------------------------------------------------------------------
// Group key helpers — array keys with prefix matching
// ---------------------------------------------------------------------------

export type ImageGroupKey = string[];

export function serializeKey(key: ImageGroupKey): string {
  return JSON.stringify(key);
}

export function pathnameToKey(pathname: string): ImageGroupKey {
  return ["route", ...pathname.split("/").filter(Boolean)];
}

function isPrefix(prefix: ImageGroupKey, key: ImageGroupKey): boolean {
  if (prefix.length > key.length) return false;
  return prefix.every((seg, i) => key[i] === seg);
}

function isSameBranch(a: ImageGroupKey, b: ImageGroupKey): boolean {
  return isPrefix(a, b) || isPrefix(b, a);
}

// ---------------------------------------------------------------------------
// JS-side group registry — tracks which serialized keys are currently mounted
// ---------------------------------------------------------------------------

const mountedGroups = new Map<string, number>(); // serializedKey → refcount

export function registerGroup(serializedKey: string) {
  mountedGroups.set(serializedKey, (mountedGroups.get(serializedKey) ?? 0) + 1);
}

export function unregisterGroup(serializedKey: string) {
  const count = mountedGroups.get(serializedKey) ?? 0;
  if (count <= 1) {
    mountedGroups.delete(serializedKey);
  } else {
    mountedGroups.set(serializedKey, count - 1);
  }
}

// ---------------------------------------------------------------------------
// Epoch store — bumped when a group becomes active again after cancellation
// ---------------------------------------------------------------------------

const epochs = new Map<string, number>();
let epochListeners = new Set<() => void>();

function getEpoch(serializedKey: string): number {
  return epochs.get(serializedKey) ?? 0;
}

function bumpEpoch(serializedKey: string) {
  epochs.set(serializedKey, getEpoch(serializedKey) + 1);
  epochListeners.forEach((l) => l());
}

function subscribeEpochs(listener: () => void) {
  epochListeners.add(listener);
  return () => {
    epochListeners.delete(listener);
  };
}

export function useGroupEpoch(serializedKey: string | undefined): number {
  return useSyncExternalStore(
    subscribeEpochs,
    () => (serializedKey ? getEpoch(serializedKey) : 0),
    () => 0,
  );
}

// ---------------------------------------------------------------------------
// Global scheduler — called on active group change
// ---------------------------------------------------------------------------

let currentActiveKey: ImageGroupKey | null = null;
let isRunning = false;
const cancelledGroups = new Set<string>();

export function setActiveImageGroup(key: ImageGroupKey) {
  const prev = currentActiveKey;
  currentActiveKey = key;

  if (prev && serializeKey(prev) === serializeKey(key)) {
    return;
  }

  if (!isRunning) {
    runScheduler().catch((error) => {
      captureException(error, {
        channel: "ignore",
        area: "image",
        operation: "run-image-request-scheduler",
      });
    });
  }
}

async function runScheduler() {
  isRunning = true;
  let lastProcessedKey: string | null = null;

  try {
    // Loop until stable — if the active key changes during an async cancel
    // pass, we re-run with the latest key instead of dropping the update.
    while (true) {
      const active = currentActiveKey;
      if (!active) break;

      const activeSerialized = serializeKey(active);
      if (activeSerialized === lastProcessedKey) break;
      lastProcessedKey = activeSerialized;

      // Cancel groups not in the active branch
      const cancelled: string[] = [];
      const revived: string[] = [];

      for (const serializedKey of [...mountedGroups.keys()]) {
        const parsed: ImageGroupKey = JSON.parse(serializedKey);
        if (!isSameBranch(active, parsed)) {
          await Image.cancelRequestGroup(serializedKey).catch((error) => {
            captureException(error, {
              channel: "ignore",
              area: "image",
              operation: "cancel-image-request-group",
            });
          });
          cancelledGroups.add(serializedKey);
          cancelled.push(parsed.join("/"));
        }
      }

      // Bump epochs only for previously-cancelled groups that are now
      // back in the active branch — avoids unnecessary recyclingKey
      // changes (and flicker) for images that were never cancelled.
      for (const serializedKey of [...cancelledGroups]) {
        const parsed: ImageGroupKey = JSON.parse(serializedKey);
        if (isSameBranch(active, parsed)) {
          bumpEpoch(serializedKey);
          cancelledGroups.delete(serializedKey);
          revived.push(parsed.join("/"));
        }
      }

      console.log(
        `[ImageScheduler] active=${active.join("/")} cancelled=[${cancelled.join(", ")}] revived=[${revived.join(", ")}] mounted=${[...mountedGroups.keys()].map((k) => JSON.parse(k).join("/")).join(", ")}`,
      );
    }
  } catch (e) {
    captureException(e, {
      channel: "ignore",
      area: "image",
      operation: "image-request-scheduler",
    });
  } finally {
    isRunning = false;
  }
}

// ---------------------------------------------------------------------------
// Context for overriding the default group key within a subtree
// ---------------------------------------------------------------------------

const ImageGroupKeyOverrideContext = createContext<ImageGroupKey | undefined>(
  undefined,
);

export function useImageGroupKeyOverride(): ImageGroupKey | undefined {
  return useContext(ImageGroupKeyOverrideContext);
}

export function ImageGroupKeyProvider({
  children,
  groupKey,
}: {
  children: React.ReactNode;
  groupKey: ImageGroupKey;
}) {
  return (
    <ImageGroupKeyOverrideContext.Provider value={groupKey}>
      {children}
    </ImageGroupKeyOverrideContext.Provider>
  );
}
